import React from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/AppContext';
import { marked } from 'marked'; // Optional: if we want to render markdown. I will use simple line breaks or just pre-wrap. Let's just output text with whitespace preserved for simplicity or use dangerouslySetInnerHTML with formatted text.

export const LegalPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') as 'privacy' | 'terms' | 'legal' | null;
    const [activeTab, setActiveTab] = React.useState<'privacy' | 'terms' | 'legal'>(initialTab || 'privacy');

    return (
        <div className="p-4 pt-14 space-y-6 w-full animate-fadeIn">
            <PageHeader
                title={t('legal.title', 'Informations Légales')}
                icon="gavel"
            />

            {/* Tab Navigation */}
            <div className="flex bg-surface-2 rounded-xl p-1 border border-glass-border">
                <button
                    onClick={() => setActiveTab('privacy')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'privacy'
                        ? 'bg-primary text-white shadow-md'
                        : 'text-text-secondary hover:text-text-primary'
                        }`}
                >
                    {t('legal.tabs.privacy', 'Confidentialité')}
                </button>
                <button
                    onClick={() => setActiveTab('terms')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'terms'
                        ? 'bg-primary text-white shadow-md'
                        : 'text-text-secondary hover:text-text-primary'
                        }`}
                >
                    {t('legal.tabs.terms', 'CGU')}
                </button>
                <button
                    onClick={() => setActiveTab('legal')}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'legal'
                        ? 'bg-primary text-white shadow-md'
                        : 'text-text-secondary hover:text-text-primary'
                        }`}
                >
                    {t('legal.tabs.legal', 'Mentions')}
                </button>
            </div>

            {/* Content */}
            <div className="bg-surface-2 rounded-xl p-6 border border-glass-border">
                {activeTab === 'privacy' && <PrivacyPolicy />}
                {activeTab === 'terms' && <TermsOfService />}
                {activeTab === 'legal' && <LegalNotice />}
            </div>
        </div>
    );
};

export const PrivacyPolicy: React.FC = () => {
    const { t } = useTranslation();
    const { db } = useAppContext();
    const wl = db.globalConfig?.whiteLabel;

    if (wl?.enabled && wl.legal?.privacyText) {
        return (
            <div className="prose prose-invert max-w-none space-y-6">
                <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">shield</span>
                    {t('legal.tabs.privacy', 'Confidentialité')}
                </h2>
                <div className="whitespace-pre-wrap text-sm text-text-secondary">
                    {wl.legal.privacyText}
                </div>
            </div>
        );
    }

    const sections = t('legalContent.privacy.sections', { returnObjects: true }) as Array<{ title: string, content: string }>;
    return (
        <div className="prose prose-invert max-w-none space-y-6">
            <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">shield</span>
                {t('legalContent.privacy.title')}
            </h2>
            <p className="text-text-secondary text-sm">{t('legalContent.privacy.lastUpdated')}</p>

            {Array.isArray(sections) && sections.map((sec, idx) => (
                <section key={idx} className="space-y-3">
                    <h3 className="text-lg font-semibold text-text-primary">{sec.title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: sec.content }} />
                </section>
            ))}
        </div>
    );
};

export const TermsOfService: React.FC = () => {
    const { t } = useTranslation();
    const { db } = useAppContext();
    const wl = db.globalConfig?.whiteLabel;

    if (wl?.enabled && wl.legal?.termsText) {
        return (
            <div className="prose prose-invert max-w-none space-y-6">
                <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">description</span>
                    {t('legal.tabs.terms', 'CGU')}
                </h2>
                <div className="whitespace-pre-wrap text-sm text-text-secondary">
                    {wl.legal.termsText}
                </div>
            </div>
        );
    }

    const sections = t('legalContent.terms.sections', { returnObjects: true }) as Array<{ title: string, content: string }>;
    return (
        <div className="prose prose-invert max-w-none space-y-6">
            <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">description</span>
                {t('legalContent.terms.title')}
            </h2>
            <p className="text-text-secondary text-sm">{t('legalContent.terms.lastUpdated')}</p>

            {Array.isArray(sections) && sections.map((sec, idx) => (
                <section key={idx} className="space-y-3">
                    <h3 className="text-lg font-semibold text-text-primary">{sec.title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: sec.content }} />
                </section>
            ))}
        </div>
    );
};

const LegalNotice: React.FC = () => {
    const { t } = useTranslation();
    const { db } = useAppContext();
    const wl = db.globalConfig?.whiteLabel;

    if (wl?.enabled && wl.legal?.aboutText) {
        return (
            <div className="prose prose-invert max-w-none space-y-6">
                <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">info</span>
                    {t('legal.tabs.legal', 'Mentions')}
                </h2>
                <div className="whitespace-pre-wrap text-sm text-text-secondary">
                    {wl.legal.aboutText}
                </div>
            </div>
        );
    }

    const sections = t('legalContent.notices.sections', { returnObjects: true }) as Array<{ title: string, content: string }>;
    return (
        <div className="prose prose-invert max-w-none space-y-6">
            <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">info</span>
                {t('legalContent.notices.title')}
            </h2>
            <p className="text-text-secondary text-sm">
                {t('legalContent.notices.lastUpdated')}
            </p>

            {Array.isArray(sections) && sections.map((sec, idx) => (
                <section key={idx} className="space-y-3">
                    <h3 className="text-lg font-semibold text-text-primary">{sec.title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: sec.content }} />
                </section>
            ))}
        </div>
    );
};
